// WHAT: Control component for timeline navigation, zoom, and module filtering
// WHY:  Provides user interface for navigating timeline view and adjusting zoom level
// DOMAIN: Admin → Roadmap → Timeline
// DEPENDENCIES: Stack, Button, Select components, Lucide icons
// RISKS:
//   - NOTE: Date formatting uses 'de-DE' locale - may not match user preference
//   - NOTE: Zoom level display assumes specific values (1=Month, 2=Week, 3=Day)
//   - NOTE: Module filter includes 'all' option - ensure parent handles this correctly

import {
  LucideChevronLeft,
  LucideChevronRight,
  LucideZoomIn,
  LucideZoomOut,
  LucideFilter,
} from "lucide-react";
import React from "react";

import { Stack, Button, Select } from "@/components/ui";

/**
 * Props for TimelineControls component
 */
interface TimelineControlsProperties {
  /** Currently displayed month */
  currentMonth: Date;
  /** Available modules for filtering */
  modules: string[];
  /** Handler for module filter change */
  onModuleChange: (module: string) => void;
  /** Handler for next month navigation */
  onNextMonth: () => void;
  /** Handler for previous month navigation */
  onPreviousMonth: () => void;
  /** Handler to jump to current month */
  onToday: () => void;
  /** Handler to zoom in */
  onZoomIn: () => void;
  /** Handler to zoom out */
  onZoomOut: () => void;
  /** Currently selected module filter */
  selectedModule: string;
  /** Current zoom level (1=Month, 2=Week, 3=Day) */
  zoomLevel: number;
}

/**
 * WHAT:
 *   Control component providing navigation, zoom, and filtering controls
 *   for the timeline view.
 *
 * WHY:
 *   Enables users to navigate through time, adjust zoom level, and filter
 *   by module in the timeline view.
 *
 * PROPS:
 *   - currentMonth: Date — Currently displayed month
 *   - zoomLevel: number — Current zoom level (1=Month, 2=Week, 3=Day)
 *   - selectedModule: string — Selected module filter
 *   - modules: string[] — Available modules
 *   - onPreviousMonth: () => void — Previous month handler
 *   - onNextMonth: () => void — Next month handler
 *   - onToday: () => void — Jump to today handler
 *   - onZoomIn: () => void — Zoom in handler
 *   - onZoomOut: () => void — Zoom out handler
 *   - onModuleChange: (module: string) => void — Module filter change handler
 *
 * BEHAVIOR:
 *   - Displays current month in German locale
 *   - Shows zoom level as text (Month/Week/Day)
 *   - Provides navigation buttons (previous, today, next)
 *   - Provides zoom controls (zoom out, zoom in)
 *   - Provides module filter dropdown
 *
 * RISKS:
 *   - NOTE: Date formatting hardcoded to 'de-DE' - consider user locale preference
 *   - NOTE: Zoom level display assumes specific values - may break if zoom levels change
 *   - NOTE: Module filter 'all' option must be handled by parent component
 *
 * @param root0
 *
 * @param root0.currentMonth
 *
 * @param root0.zoomLevel
 *
 * @param root0.selectedModule
 *
 * @param root0.modules
 *
 * @param root0.onPreviousMonth
 *
 * @param root0.onNextMonth
 *
 * @param root0.onToday
 *
 * @param root0.onZoomIn
 *
 * @param root0.onZoomOut
 *
 * @param root0.onModuleChange
 *
 * @returns Timeline controls component with navigation, zoom, and filtering
 *
 */
export const TimelineControls: React.FC<TimelineControlsProperties> = ({
  currentMonth,
  modules,
  onModuleChange,
  onNextMonth,
  onPreviousMonth,
  onToday,
  onZoomIn,
  onZoomOut,
  selectedModule,
  zoomLevel,
}) => {
  return (
    <div className="rounded-tracq p-6 bg-slate-700/30 border border-slate-600/50 shadow-tracq-card">
      <Stack align="center" className="mb-6" direction="row" justify="between">
        {/* Navigation */}
        <Stack align="center" direction="row" gap={4}>
          <Button
            className="p-2 text-slate-300 hover:text-white hover:bg-slate-600/50 rounded-lg transition-all duration-200"
            onClick={onPreviousMonth}
            size="sm"
            variant="ghost"
          >
            <LucideChevronLeft className="w-4 h-4" />
          </Button>

          <Button
            className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
            onClick={onToday}
            variant="primary"
          >
            Today
          </Button>

          <Button
            className="p-2 text-slate-300 hover:text-white hover:bg-slate-600/50 rounded-lg transition-all duration-200"
            onClick={onNextMonth}
            size="sm"
            variant="ghost"
          >
            <LucideChevronRight className="w-4 h-4" />
          </Button>

          <span className="text-white font-semibold">
            {currentMonth.toLocaleDateString("de-DE", {
              month: "long",
              year: "numeric",
            })}
          </span>
        </Stack>

        {/* Zoom Controls */}
        <Stack align="center" direction="row" gap={2}>
          <Button
            className="p-2 text-slate-300 hover:text-white hover:bg-slate-600/50 rounded-lg transition-all duration-200"
            onClick={onZoomOut}
            size="sm"
            variant="ghost"
          >
            <LucideZoomOut className="w-4 h-4" />
          </Button>

          <span className="text-sm text-slate-300 px-3">
            {(() => {
              if (zoomLevel === 1) {
                return "Month";
              }
              if (zoomLevel === 2) {
                return "Week";
              }
              return "Day";
            })()}
          </span>

          <Button
            className="p-2 text-slate-300 hover:text-white hover:bg-slate-600/50 rounded-lg transition-all duration-200"
            onClick={onZoomIn}
            size="sm"
            variant="ghost"
          >
            <LucideZoomIn className="w-4 h-4" />
          </Button>
        </Stack>
      </Stack>

      {/* Module Filter */}
      <Stack align="center" className="mb-6" direction="row" gap={4}>
        <Stack align="center" direction="row" gap={2}>
          <LucideFilter className="w-4 h-4 text-slate-300" />
          <span className="text-sm text-slate-300">Module:</span>
        </Stack>

        <Select
          className="min-w-[200px]"
          onChange={(value) => {
            onModuleChange(value as string);
          }}
          options={[
            { label: "All Modules", value: "all" },
            ...modules.map((module) => ({ label: module, value: module })),
          ]}
          placeholder="Select module..."
          size="sm"
          value={selectedModule}
        />
      </Stack>
    </div>
  );
};
